import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js"

// Detects `| render TYPE` at the end of the current query.
// When results stream in, strips the table and renders a Chart.js visualization instead.
// Supported types: timechart, barchart, piechart, scatterchart, areachart
const RENDER_RE = /\|\s*render\s+(timechart|barchart|areachart|piechart|scatterchart)\s*$/i

const CHART_COLORS = [
  "#F97316","#3B82F6","#22C55E","#A855F7","#F59E0B",
  "#EF4444","#14B8A6","#60A5FA","#86EFAC","#FCD34D",
]

export default class extends Controller {
  #chart = null
  #boundCheck = null
  #observer = null

  connect() {
    this.#boundCheck = () => this.#maybeRender()
    document.addEventListener("turbo:before-stream-render", this.#boundCheck)
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.#boundCheck)
    this.#chart?.destroy()
    this.#chart = null
    this.#observer?.disconnect()
  }

  #maybeRender() {
    const query = document.querySelector("textarea[name='query']")?.value?.trim() || ""
    const match = query.match(RENDER_RE)
    if (!match) {
      this.#destroyChart()
      return
    }
    const renderType = match[1].toLowerCase()
    // Use a MutationObserver to wait for the DOM replacement to land
    this.#observer?.disconnect()
    this.#observer = new MutationObserver(() => {
      this.#observer.disconnect()
      this.#renderChart(renderType)
    })
    const tablePane = this.element.querySelector(".siem-table-pane")
    if (tablePane) this.#observer.observe(tablePane, { childList: true, subtree: true })
  }

  #renderChart(type) {
    this.#chart?.destroy()
    this.#chart = null

    const table = this.element.querySelector(".siem-table-pane table")
    if (!table) return

    const { labels, datasets } = this.#parseTable(table)
    if (!labels.length || !datasets.length) return

    const container = this.#injectCanvas(table)
    if (!container) return

    const ctx = container.querySelector("canvas").getContext("2d")

    switch (type) {
    case "timechart":
    case "areachart":
      this.#buildLineChart(ctx, labels, datasets, type === "areachart")
      break
    case "barchart":
      this.#buildBarChart(ctx, labels, datasets)
      break
    case "piechart":
      this.#buildPieChart(ctx, labels, datasets)
      break
    case "scatterchart":
      this.#buildScatterChart(ctx, labels, datasets)
      break
    }
  }

  // Reads the visible table. First column = labels, remaining numeric columns = datasets.
  #parseTable(table) {
    const headers = [...table.querySelectorAll("thead th")].map(th => th.textContent.trim())
    const rows = [...table.querySelectorAll("tbody tr")]

    const labels = []
    const seriesData = headers.slice(1).map(() => [])

    rows.forEach(row => {
      const cells = [...row.querySelectorAll("td")]
      if (!cells.length) return
      labels.push(cells[0]?.textContent.trim() || "")
      headers.slice(1).forEach((_, i) => {
        const val = parseFloat(cells[i + 1]?.textContent.replace(/,/g, "") || "0")
        seriesData[i].push(isNaN(val) ? 0 : val)
      })
    })

    const datasets = headers.slice(1).map((name, i) => ({
      label: name,
      data: seriesData[i],
      backgroundColor: CHART_COLORS[i % CHART_COLORS.length] + "99",
      borderColor: CHART_COLORS[i % CHART_COLORS.length],
      borderWidth: 2,
      fill: false,
      lineTension: 0.3,
      pointRadius: 3,
    }))

    return { labels, datasets }
  }

  #injectCanvas(table) {
    this.element.querySelector(".siem-render-output")?.remove()
    window.dispatchEvent(new CustomEvent("siem:viz-chart-clear"))

    const wrap = document.createElement("div")
    wrap.className = "siem-render-output"
    wrap.innerHTML = `
      <div class="siem-render-label">
        <i class="fa fa-bar-chart"></i>
        Rendered output
        <button class="siem-render-close" type="button" title="Close chart">
          <i class="fa fa-times"></i>
        </button>
      </div>
      <div class="siem-render-chart-wrap">
        <canvas></canvas>
      </div>
    `

    const injectEvent = new CustomEvent("siem:viz-chart-inject", { detail: { wrap }, cancelable: true })
    window.dispatchEvent(injectEvent)

    if (!injectEvent.defaultPrevented) {
      wrap.querySelector(".siem-render-close").addEventListener("click", () => {
        this.#destroyChart()
        wrap.remove()
      })
      table.closest(".panel")?.after(wrap) || table.parentElement?.after(wrap)
    }
    return wrap
  }

  #buildLineChart(ctx, labels, datasets, fill) {
    datasets.forEach(d => { d.fill = fill })
    this.#chart = new Chart(ctx, {
      type: "line",
      data: { labels, datasets },
      options: this.#baseOptions(),
    })
  }

  #buildBarChart(ctx, labels, datasets) {
    this.#chart = new Chart(ctx, {
      type: labels.length > 8 ? "horizontalBar" : "bar",
      data: { labels, datasets },
      options: this.#baseOptions(),
    })
  }

  #buildPieChart(ctx, labels, datasets) {
    const values = datasets[0]?.data || []
    this.#chart = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels,
        datasets: [{
          data: values,
          backgroundColor: CHART_COLORS.slice(0, values.length).map(c => c + "cc"),
          borderColor: "#1E293B",
          borderWidth: 2,
        }],
      },
      options: {
        responsive: true, maintainAspectRatio: false, cutoutPercentage: 50,
        legend: { position: "right", labels: { boxWidth: 12, fontSize: 11 } },
      },
    })
  }

  #buildScatterChart(ctx, labels, datasets) {
    // X = first numeric column value, Y = second numeric column value
    const xData = datasets[0]?.data || []
    const yData = datasets[1]?.data || xData
    const points = labels.map((label, i) => ({ x: xData[i] || 0, y: yData[i] || 0, label }))

    this.#chart = new Chart(ctx, {
      type: "bubble",
      data: {
        datasets: [{
          label: datasets[0]?.label || "values",
          data: points.map(p => ({ x: p.x, y: p.y, r: 6 })),
          backgroundColor: CHART_COLORS[0] + "99",
          borderColor: CHART_COLORS[0],
        }],
      },
      options: {
        ...this.#baseOptions(),
        tooltips: { callbacks: { label: (tooltipItem) => `${labels[tooltipItem.index]}: (${tooltipItem.xLabel}, ${tooltipItem.yLabel})` } },
      },
    })
  }

  #baseOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      legend: { labels: { fontColor: "#94A3B8", boxWidth: 12, fontSize: 11 } },
      scales: {
        xAxes: [{ ticks: { fontColor: "#64748B", maxTicksLimit: 12 }, gridLines: { color: "#2D3B4E" } }],
        yAxes: [{ ticks: { fontColor: "#64748B" }, gridLines: { color: "#2D3B4E" } }],
      },
    }
  }

  #destroyChart() {
    this.#chart?.destroy()
    this.#chart = null
    window.dispatchEvent(new CustomEvent("siem:viz-chart-destroy"))
  }
}
